// Wire the scene graph: axes/coordinate vectors, axis grid, slice planes,
// selection geometry, lighting, colorbar HUD, coordinate-system overlay.
// Pure scene + UI-overlay setup that depends on viewer.field being loaded.

import * as THREE from 'three';
import { params } from '@store/instance';
import { mapPhysicalToThreeWithRotation, threeJSToPhysical, physicalToThreeJS } from '@adapters/coordinates';
import { getFieldValue } from '@physics/probe';
import { strengthToColor } from '@physics/colormap';
import { initSelection } from '@scene/picking';
import { createAllSlices } from '@scene/slices';
import { applyAxisGrid } from '@scene/grid';
import { applyCoordinateSystem, applyAxesVectors } from '@scene/axes';
import { setupLighting } from '@render/lighting';
import { mountColorbar } from '../colormapSync';
import { mountColorbarSettings } from '@ui/colorbarSettings';
import { mountCoordinateSystemDisplay } from '@ui/coordinateDisplay';
import type { ViewerContext } from '../context';
import type { ObjectRegistry } from '../objectRegistry';

export function wireSceneGraph(viewer: ViewerContext): void {
    const scene = viewer.three.scene;
    const camera = viewer.three.camera;
    const renderer = viewer.three.renderer;
    if (!scene || !camera || !renderer) return;

    if (viewer.sceneMeta.axisConfigPresent) {
        viewer.grid.axisExtents = {
            x: { min: viewer.grid.xMin, max: viewer.grid.xMax },
            y: { min: viewer.grid.yMin, max: viewer.grid.yMax },
            z: { min: viewer.grid.zMin, max: viewer.grid.zMax },
        };
        if (viewer.sceneMeta.showCoordAxes && viewer.sceneMeta.axesVectorsConfig) {
            applyAxesVectors(viewer, viewer.sceneMeta.axesVectorsConfig as unknown as Parameters<typeof applyAxesVectors>[1]);
        } else if (viewer.sceneMeta.showCoordAxes) {
            applyCoordinateSystem(viewer, viewer.objects.sceneRegistry as unknown as ObjectRegistry | null);
        }
        applyAxisGrid(viewer, params);
    }

    createAllSlices(viewer, params);

    const initSelOut = initSelection({
        THREE,
        scene,
        SCENE_SELECTION_SPHERE_RADIUS: viewer.sceneMeta.selection.sphereRadius,
        SCENE_SELECTION_POSITION: viewer.sceneMeta.selection.position,
        THEME: viewer.theme.current,
        SCENE_SELECTION_PROJECTION_COLOR: viewer.sceneMeta.selection.projectionColor,
        SCENE_SELECTION_PROJECTION_OPACITY: viewer.sceneMeta.selection.projectionOpacity,
        mapPhysicalToThreeWithRotation,
        threeJSToPhysical,
        getFieldValue: (x, y, z) => getFieldValue(viewer, x, y, z),
        fieldStrengthToColor: (s) => strengthToColor(viewer, params, s),
        UNITS: viewer.sceneMeta.units,
        fieldMetadata: viewer.field.metadata,
        cubeSize: viewer.objects.selection.size,
        showSelectionCube: !!params.showSelectionCube,
    });
    Object.assign(viewer.objects.selection, {
        invisibleMesh: initSelOut.invisibleCubeMesh,
        wireframe: initSelOut.cubeWireframe,
        centerDot: initSelOut.cubeCenterDot,
        cube: initSelOut.selectionCube,
        ring: initSelOut.selectionRing,
        projectionLines: initSelOut.projectionLines,
    });

    mountColorbar(viewer, params);
    mountColorbarSettings(viewer);
    mountCoordinateSystemDisplay(viewer);
    setupLighting({
        THREE, scene, mapPhysicalToThreeWithRotation,
        lighting: viewer.sceneMeta.lighting,
    });

    // Re-use physicalToThreeJS to silence the "unused import" warning when this
    // phase grows to need it; the import is intentionally kept because the
    // input-phase initSelectionInteractions passes it in.
    void physicalToThreeJS;
}
